import { BaseCarouselProps } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIGridRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/SDUIGridRenderer";
import { isNotUndefined } from "@faire/typescript";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { IComponentLoggingData } from "@faire/web-api--source/indigofair/data/IComponentLoggingData";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { IImageElement } from "@faire/web-api--source/indigofair/data/IImageElement";
import { IImageTile } from "@faire/web-api--source/indigofair/data/IImageTile";
import { INavigateAction } from "@faire/web-api--source/indigofair/data/INavigateAction";
import { IResponsiveInt } from "@faire/web-api--source/indigofair/data/IResponsiveInt";
import { ITextElement } from "@faire/web-api--source/indigofair/data/ITextElement";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import { ITileLoggingData } from "@faire/web-api--source/indigofair/data/ITileLoggingData";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import { TextStyleType } from "@faire/web-api--source/indigofair/data/TextStyleType";
import * as React from "react";
import { useMemo } from "react";

export const DynamicSparseGrid: React.FC<BaseCarouselProps> = () => {
  const { layoutElement, requestId } = React.useContext(Context);

  const sduiTiles = useMemo(
    () =>
      layoutElement.layout_element_data
        .map(
          (layoutElementData) => layoutElementData.dynamic_grid_item_rectangle
        )
        .filter(isNotUndefined)
        .map((gridItem, index) =>
          ITile.build({
            image_tile: IImageTile.build({
              below_image_title: ITextElement.build({
                text: gridItem.below_image_text,
                style: ITextElement.ITextElementStyle.build({
                  type: TextStyleType.PARAGRAPH,
                  color: ColorTokenKey.CORE_TEXT_PRIMARY,
                }),
              }),
              image_element: IImageElement.build({
                image: gridItem.image,
              }),
              style: IImageTile.IImageTileStyle.build({
                variant: IImageTile.ImageTileVariant.LANDSCAPE_TILE,
                animation_on_hover: [
                  IImageTile.ImageTileAnimation.ZOOM,
                  IImageTile.ImageTileAnimation.UNDERLINE,
                ],
              }),
            }),
            logging_data: ITileLoggingData.build({
              tile_position: index,
              instance_identifier: gridItem.unique_identifier,
            }),
            on_click: [
              IAction.build({
                navigate: INavigateAction.build({
                  link: gridItem.cta_link,
                }),
              }),
            ],
          })
        ),
    [layoutElement]
  );

  const sduiLoggingEventParams = useMemo(
    () =>
      ComponentLoggingEventParams.build({
        view_display_page: DisplayPageType.CATEGORY_PAGE,
        component_name: layoutElement.on_click_event_name,
        request_id: requestId,
      }),
    [layoutElement, requestId]
  );

  const sduiComponent = useMemo(
    () =>
      IComponent.build({
        logging_data: IComponentLoggingData.build({
          component_name: layoutElement.on_click_event_name,
        }),
        grid_component: IGridComponent.build({
          title: ITextElement.build({
            text: layoutElement.main_text,
            style: ITextElement.ITextElementStyle.build({
              type: TextStyleType.PAGE_HEADER,
            }),
          }),
          column_layout: IGridComponent.IColumnLayout.build({
            fixed: IGridComponent.IFixedColumnLayout.build({
              responsive_num_columns: IResponsiveInt.build({
                mobile_and_above: 1,
                tablet_and_above: 2,
                desktop_and_above: 3,
              }),
            }),
          }),
          style: IGridStyle.build({
            column_spacing: Spacing.R,
            row_spacing: Spacing.L,
          }),
          tiles: sduiTiles,
        }),
      }),
    [layoutElement, sduiTiles]
  );

  return (
    <SDUIGridRenderer
      component={sduiComponent}
      loggingData={sduiLoggingEventParams}
    />
  );
};
